import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import { Stack } from "@mui/material";
import {
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import { useCallback, useEffect, useState } from "react";

import {
  BoldButton,
  CenterAlignButton,
  ItalicButton,
  JustifyAlignButton,
  LeftAlignButton,
  RedoButton,
  RightAlignButton,
  StrikethroughButton,
  ToolbarDivider,
  UnderlineButton,
  UndoButton,
} from "../components/toolbarButtons";

export function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [alignment, setAlignment] = useState<string>("left");

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(
          () => {
            $updateToolbar();
          },
          { editor }
        );
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, $updateToolbar]);

  return (
    <Stack direction="row" spacing={1} pt={1}>
      <UndoButton editor={editor} disabled={!canUndo} />
      <RedoButton editor={editor} disabled={!canRedo} />

      <ToolbarDivider />

      <BoldButton editor={editor} isActive={isBold} />
      <ItalicButton editor={editor} isActive={isItalic} />
      <UnderlineButton editor={editor} isActive={isUnderline} />
      <StrikethroughButton editor={editor} isActive={isStrikethrough} />

      <ToolbarDivider />

      <LeftAlignButton editor={editor} isActive={alignment === "left"} onAlignmentChange={setAlignment} />
      <CenterAlignButton editor={editor} isActive={alignment === "center"} onAlignmentChange={setAlignment} />
      <RightAlignButton editor={editor} isActive={alignment === "right"} onAlignmentChange={setAlignment} />
      <JustifyAlignButton editor={editor} isActive={alignment === "justify"} onAlignmentChange={setAlignment} />
    </Stack>
  );
}
